// OPTIONS ACCESS (admin) - one compact panel per broker account: what it trades (products), the
// four Options switches, its account limits, the verified option quantities, and the last change.
// Every change is audited and needs a reason (SHADOW / LIVE / products are flagged as significant).
// Configuration only: no broker-write gate is changed here, and SHADOW / LIVE are never defaulted on.
import React, { useCallback, useEffect, useState } from "react";
import { Layers, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import optionsApi from "@/lib/optionsApi";
import { dateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorBox, Field, dangerBtn, ghostBtn, inputCls, primaryBtn } from "@/components/auth/AuthUi";

const FEATURES = [
  { key: "ANALYSIS", field: "options_analysis_enabled", label: "Options Analysis", hint: "the Scanner may run for this account" },
  { key: "HEDGE", field: "options_hedge_enabled", label: "Options Hedging", hint: "hedge trades may be created" },
  { key: "SHADOW", field: "options_shadow_enabled", label: "Options SHADOW", hint: "real orders to an unfunded account, simulated fills", danger: true },
  { key: "LIVE", field: "options_live_enabled", label: "Options LIVE", hint: "real money", danger: true },
  { key: "SELL_LIVE", field: "options_sell_live_enabled", label: "Options LIVE SELL", hint: "real-money SELL OPTIONS (on top of Options LIVE); revoking stops new SELL entries at once, exits continue", danger: true },
  { key: "IRON_CONDOR", field: "ic_scanner_enabled", label: "Box & Iron Condor · Iron Condor scan", hint: "read-only Iron Condor scanner (no orders)" },
  { key: "BOX_SCANNER", field: "box_scanner_enabled", label: "Box & Iron Condor · Box Spread scan", hint: "read-only Box Spread scanning (no orders); never set = follows the Iron Condor scan" },
  { key: "IC_SHADOW", field: "ic_shadow_enabled", label: "Iron Condor SHADOW", hint: "real orders to the unfunded SHADOW account, simulated fills", danger: true },
  { key: "BOX_SHADOW", field: "box_shadow_enabled", label: "Box Spread SHADOW", hint: "its own key (Iron Condor SHADOW does not authorise it): real orders to the unfunded SHADOW account, simulated fills", danger: true },
  { key: "IC_LIVE", field: "ic_live_enabled", label: "Iron Condor LIVE", hint: "real money: on top of Options LIVE; SELL legs also need Options LIVE SELL; revoking stops new entries, exits continue", danger: true },
  { key: "BOX_LIVE", field: "box_live_enabled", label: "Box Spread LIVE", hint: "real money: on top of Options LIVE; SELL legs also need Options LIVE SELL; revoking stops new entries, exits continue", danger: true },
  { key: "RESCUE_LIVE", field: "rescue_live_enabled", label: "Dynamic Adaptive Rescue LIVE", hint: "LIVE only - real-money protective BUYs on LIVE SELL pairs with Rescue on. Not a SHADOW control: Rescue SHADOW follows Options SHADOW. Also needs OPTIONS_RESCUE_ENABLED on the server.", danger: true },
];
const PRODUCTS = [
  { key: "FUTURES_ONLY", label: "Futures" },
  { key: "OPTIONS_ONLY", label: "Options" },
  { key: "BOTH", label: "Both" },
];
const FIELD_LABEL = { ...Object.fromEntries(FEATURES.map((f) => [f.field, f.label])), trading_products: "Trading products" };
const row = "flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 border-t border-[var(--term-border)] text-sm";
// the account keys, grouped by what they authorise (29 Oct 2026 Access redesign); order inside a group = FEATURES order
export const KEY_GROUPS = [
  ["Analysis and scanners", "Read-only: scanning never places an order.", ["ANALYSIS", "IRON_CONDOR", "BOX_SCANNER"]],
  ["Options Hedging", "Allows BUY / SELL Options trades to be created (orders still need a SHADOW or LIVE key).", ["HEDGE"]],
  ["SHADOW execution keys", "Real order requests to Zerodha on an unfunded account, expected to be rejected; simulated fills. Dynamic Adaptive Rescue SHADOW follows Options SHADOW (no key of its own).", ["SHADOW", "IC_SHADOW", "BOX_SHADOW"]],
  ["LIVE execution keys - real money", "Never on an account that holds a SHADOW key.", ["LIVE", "SELL_LIVE", "IC_LIVE", "BOX_LIVE", "RESCUE_LIVE"]],
];

// why a toggle is unavailable, mirroring the server rules (the server re-checks every one)
export function blockedReason(feature, account, hasLive) {
  if (account.link_status !== "ACTIVE") return "Account is not actively linked";
  if ((feature === "IRON_CONDOR" || feature === "BOX_SCANNER") && !account.options_analysis_enabled) return "Enable Options Analysis first";
  if (feature === "SELL_LIVE" && !account.options_live_enabled) return "Enable Options LIVE first";
  if (feature === "RESCUE_LIVE" && !account.options_sell_live_enabled) return "Enable Options LIVE SELL first";
  if (feature === "IC_SHADOW" || feature === "BOX_SHADOW") {
    if (!account.options_shadow_enabled) return "Enable Options SHADOW first";
    if (!hasLive) return "Owner needs the LIVE_TRADER role";
  }
  if (feature === "IC_LIVE" || feature === "BOX_LIVE") {
    if (!account.options_live_enabled) return "Enable Options LIVE first";
    if (!hasLive) return "Owner needs the LIVE_TRADER role";
    const on = ["shadow_enabled", "options_shadow_enabled", "ic_shadow_enabled", "box_shadow_enabled"].filter((k) => account[k]);
    if (on.length) return `Disable ${on.join(", ")} first (SHADOW and LIVE never share an account)`;
  }
  if (feature === "SHADOW" || feature === "LIVE") {
    if (!account.options_hedge_enabled) return "Enable Options Hedging first";
    if (!hasLive) return "Owner needs the LIVE_TRADER role";
    const clash = feature === "SHADOW" ? ["live_enabled", "options_live_enabled"] : ["shadow_enabled", "options_shadow_enabled"];
    const on = clash.filter((k) => account[k]);
    if (on.length) return `Disable ${on.join(", ")} first (SHADOW and LIVE never share an account)`;
  }
  return null;
}

function Switch({ on, disabled, onClick, testId, title }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} disabled={disabled} onClick={onClick} data-testid={testId} title={title}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${on ? "bg-[#00C805]" : "bg-[var(--term-s3)] border border-[var(--term-border-strong)]"}`}>
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

// trading products: a compact segmented control; a change asks for a reason (audited)
function TradingProducts({ account, busy, onChange, onSaved }) {
  const current = account.effective_trading_products;
  const [pick, setPick] = useState(null);
  const [reason, setReason] = useState("");
  const save = () => onChange("trading-products", () => api.post(`/admin/broker-accounts/${account.id}/trading-products`,
    { products: pick, reason: reason.trim() }).then(() => { toast.success("Trading products saved"); setPick(null); setReason(""); return onSaved(); }));
  return (
    <div className="space-y-1" data-testid={`trading-products-${account.id}`}>
      <div className={row} style={{ borderTop: "none" }}>
        <span className="w-44 font-semibold">Trading products</span>
        <div className="inline-flex rounded-sm border border-[var(--term-border-strong)] overflow-hidden">
          {PRODUCTS.map((p) => (
            <button key={p.key} type="button" disabled={!!busy || p.key === current} data-testid={`trading-products-${p.key}-${account.id}`}
              onClick={() => setPick(p.key)}
              className={`px-3 py-1 text-[11px] font-bold ${p.key === (pick || current) ? "bg-[#2563EB] text-white" : "bg-[var(--term-s1)] text-[var(--term-secondary)]"}`}>{p.label}</button>
          ))}
        </div>
        {!account.trading_products && <span className="text-[10px] text-slate-500">(never set: derived)</span>}
      </div>
      {pick && pick !== current && (
        <div className="flex items-center gap-2 pl-44 text-xs">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audited)"
            className={`${inputCls} max-w-xs`} data-testid={`trading-products-reason-${account.id}`} />
          <button type="button" className={dangerBtn} disabled={!!busy || reason.trim().length < 3} onClick={save}
            data-testid={`trading-products-save-${account.id}`}>Change</button>
          <button type="button" className={ghostBtn} onClick={() => setPick(null)}>Cancel</button>
          <span className="text-[10px] text-slate-500">refused while a trade of a product being removed is active</span>
        </div>
      )}
    </div>
  );
}

// account limits (risk_gate options_* fields): shown as values, edited in place, saved through the
// existing audited POST /risk/limits at the account's scope. Futures limits are untouched.
/** The account's MCX options access in words (the backend reports the effective mode). */
export const MCX_LABEL = (cur) => {
  const mode = cur.options_mcx_access || "NONE";
  if (mode === "ALL_MCX_OPTIONS") return "All MCX options";
  if (mode === "SELECTED") return `Selected: ${(cur.options_mcx_allowlist || []).join(", ") || "none"}`;
  return "Off";
};

// OPTIONS segment switches (backend risk_gate.OPTIONS_SEGMENTS), per account, separately for SHADOW and LIVE
export const SEGMENTS = [["NFO_STOCK", "NFO stock options"], ["NFO_INDEX", "NFO index options"], ["MCX", "MCX options"]];
export const segmentsText = (eff) => {
  if (!eff) return "—";
  if (eff.source === "LEGACY") return "per-underlying rules below (LIVE switches not set)";
  const on = SEGMENTS.filter(([k]) => eff.switches?.[k]).map(([, l]) => l);
  return `${on.length ? on.join(" · ") : "none — no options"}${eff.source === "SHADOW_DEFAULT" ? " (default: all)" : ""}`;
};

function SegmentChecks({ value, onChange, testid }) {
  return (
    <span className="flex items-center gap-3" data-testid={testid}>
      {SEGMENTS.map(([k, label]) => (
        <label key={k} className="flex items-center gap-1">
          <input type="checkbox" checked={!!value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.checked })}
            data-testid={`${testid}-${k}`} />{label}</label>))}
    </span>
  );
}

// FUTURES segment switches (backend risk_gate.FUTURES_SEGMENTS), per account, separately for SHADOW and LIVE.
// Not set = no segment rule (the futures allowlist decides, as before). They stop NEW futures entries only:
// exits, square-offs and a pair's hedge leg are never refused by them.
export const FUTURES_SEGMENTS = [["NFO", "NFO futures"], ["MCX", "MCX futures"]];
export const futuresSegmentsText = (sw) => {
  if (!sw) return "not set (allowlist decides)";
  const on = FUTURES_SEGMENTS.filter(([k]) => sw[k]).map(([, l]) => l);
  return on.length ? on.join(" · ") : "none — no new futures entries";
};

function FuturesSegments({ account, busy, onChange, onSaved }) {
  const cur = account.futures_segments || {};
  const [edit, setEdit] = useState(false);
  const [mode, setMode] = useState({ shadow: cur.shadow ? "SET" : "UNSET", live: cur.live ? "SET" : "UNSET" });
  const [sw, setSw] = useState({ shadow: cur.shadow || { NFO: true, MCX: true }, live: cur.live || { NFO: true, MCX: true } });
  const save = () => onChange("futures-segments", () => api.post("/risk/limits", {
    limits: { broker_account_id: account.id,
      futures_segments_shadow: mode.shadow === "SET" ? sw.shadow : null,
      futures_segments_live: mode.live === "SET" ? sw.live : null } })
    .then(() => { toast.success("Futures segments saved"); setEdit(false); return onSaved && onSaved(); }));
  if (!account.futures_segments) return null;                     // shown for linked accounts only
  return (
    <div className="py-1.5 border-b border-[var(--term-border)] text-xs" data-testid={`futures-segments-${account.id}`}>
      {!edit ? (
        <div className="flex items-center gap-2">
          <span className="w-44 font-semibold">Futures segments</span>
          <span className="text-[10px] text-slate-500" data-testid={`futures-segments-text-${account.id}`}>
            SHADOW: {futuresSegmentsText(cur.shadow)} · LIVE: {futuresSegmentsText(cur.live)}</span>
          <button type="button" className="ml-auto text-slate-500 hover:text-[var(--term-primary)]" onClick={() => setEdit(true)}
            data-testid={`futures-segments-edit-${account.id}`} title="Edit futures segments"><Pencil className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <div className="space-y-1.5">
          {["shadow", "live"].map((env) => (
            <div key={env} className="flex items-center gap-3">
              <span className="w-44 font-semibold">Futures {env.toUpperCase()}</span>
              <select value={mode[env]} onChange={(e) => setMode({ ...mode, [env]: e.target.value })}
                data-testid={`futures-segments-mode-${env}-${account.id}`} className="bg-transparent border border-[var(--term-border)] rounded-sm px-1">
                <option value="UNSET">Not set (allowlist decides)</option>
                <option value="SET">Only these segments</option>
              </select>
              {mode[env] === "SET" && FUTURES_SEGMENTS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-1">
                  <input type="checkbox" checked={!!sw[env][k]} data-testid={`futures-segments-${env}-${k}-${account.id}`}
                    onChange={(e) => setSw({ ...sw, [env]: { ...sw[env], [k]: e.target.checked } })} />{label}</label>))}
            </div>))}
          <div className="text-[10px] text-slate-500 pl-44">Stops NEW futures entries in a switched-off segment. Exits, square-offs and a pair's hedge leg are never refused.</div>
          <div className="flex items-center gap-2 pl-44">
            <button type="button" className={primaryBtn} disabled={!!busy} onClick={save} data-testid={`futures-segments-save-${account.id}`}>Save</button>
            <button type="button" className="text-slate-500" onClick={() => setEdit(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Read-only ACCESS MATRIX (Phase 7d): what each account may do, from the same keys and switches the backend
// enforces. A summary only - the order gate also checks allowlists, limits, quantity verification and the session.
const yes = (why) => ({ ok: true, why });
const no = (why) => ({ ok: false, why });
export function accessMatrix(a) {
  const products = a.effective_trading_products || "FUTURES_ONLY";
  const fut = products !== "OPTIONS_ONLY", opt = products !== "FUTURES_ONLY";
  const e = (k) => (a.effective || {})[k] || {};
  const fseg = (env, seg) => {
    const sw = (a.futures_segments || {})[env];
    return sw ? (sw[seg] ? null : `${seg} switched off`) : null;
  };
  const futures = (env, key, seg) => {
    if (!fut) return no(`products: ${products}`);
    if (!a[key]) return no(`${key} off`);
    const off = fseg(env, seg);
    return off ? no(off) : yes((a.futures_segments || {})[env] ? "segment on" : "allowlist decides");
  };
  const oseg = (env) => {
    const s = (a.options_limits || {})[`segments_${env}`];
    if (!s || s.source === "LEGACY") return "per-underlying rules";
    return SEGMENTS.filter(([k]) => s.switches?.[k]).map(([k]) => k).join(" · ") || "no segment on";
  };
  const opts = (key) => !opt ? no(`products: ${products}`) : !e("HEDGE").enabled ? no("Options Hedging off")
    : !e(key).enabled ? no(`${key === "SHADOW" ? "Options SHADOW" : "Options LIVE"} off`) : yes(oseg(key.toLowerCase()));
  const strat = (key, label) => !opt ? no(`products: ${products}`) : e(key).enabled ? yes(e(key).source === "FOLLOWS_IRON_CONDOR_SCANNER" ? "follows IC scanner" : "key on") : no(`${label} off`);
  const na = { na: true };
  return [
    ["NFO futures", futures("shadow", "shadow_enabled", "NFO"), futures("live", "live_enabled", "NFO")],
    ["MCX futures", futures("shadow", "shadow_enabled", "MCX"), futures("live", "live_enabled", "MCX")],
    ["Options Hedging BUY", opts("SHADOW"), opts("LIVE")],
    ["Options Hedging SELL", opts("SHADOW"), opts("LIVE").ok ? (e("SELL_LIVE").enabled ? yes("LIVE SELL on") : no("LIVE SELL off")) : opts("LIVE")],
    ["Options Scanner", strat("ANALYSIS", "Options Analysis"), na],
    ["Iron Condor scanner", strat("IRON_CONDOR", "IC scanner"), na],
    ["Box Spread scanner", strat("BOX_SCANNER", "Box scanner"), na],
    ["Iron Condor trades", strat("IC_SHADOW", "IC SHADOW"), strat("IC_LIVE", "IC LIVE")],
    ["Box Spread trades", strat("BOX_SHADOW", "Box SHADOW"), strat("BOX_LIVE", "Box LIVE")],
  ];
}

function Cell({ c, testid }) {
  if (c.na) return <td className="px-2 text-slate-500" data-testid={testid}>—</td>;
  return <td className="px-2" data-testid={testid}><b className={c.ok ? "text-[#00C805]" : "text-slate-500"}>{c.ok ? "yes" : "no"}</b>
    <span className="text-[10px] text-slate-500"> · {c.why}</span></td>;
}

function AccessMatrix({ account }) {
  if (account.link_status !== "ACTIVE") return null;
  return (
    <details className="py-1.5 border-b border-[var(--term-border)] text-xs" data-testid={`access-matrix-${account.id}`}>
      <summary className="cursor-pointer font-semibold">Access matrix (read-only)</summary>
      <table className="mt-1 w-full text-left">
        <thead><tr className="text-[10px] text-slate-500"><th className="px-2">Capability</th><th className="px-2">SHADOW</th><th className="px-2">LIVE</th></tr></thead>
        <tbody>
          {accessMatrix(account).map(([label, sh, lv]) => (
            <tr key={label}><td className="px-2">{label}</td>
              <Cell c={sh} testid={`access-${label}-shadow-${account.id}`} /><Cell c={lv} testid={`access-${label}-live-${account.id}`} /></tr>))}
        </tbody>
      </table>
      <div className="text-[10px] text-slate-500 px-2">From the account's keys and switches. Every order is still checked by the server against allowlists, limits, quantity verification and the broker session.</div>
    </details>
  );
}

function OptionsLimits({ account, busy, onChange, onSaved }) {
  const cur = account.options_limits || {};
  const [edit, setEdit] = useState(false);
  const [names, setNames] = useState((cur.options_allowlist || []).join(", "));
  const [mcxNames, setMcxNames] = useState((cur.options_mcx_allowlist || []).join(", "));
  const [mcxAccess, setMcxAccess] = useState(cur.options_mcx_access || "NONE");
  const [access, setAccess] = useState(cur.options_instrument_access || "ALL_NFO_STOCK_OPTIONS");
  const [maxLots, setMaxLots] = useState(String(cur.options_max_lots_per_order ?? 1));
  const [maxPairs, setMaxPairs] = useState(String(cur.options_max_pairs_per_trade ?? 1));
  const [maxOpen, setMaxOpen] = useState(String(cur.options_max_open_trades ?? 1));
  const [loss, setLoss] = useState(String(cur.options_daily_loss_limit ?? 0));
  const allNfo = (cur.options_instrument_access || "ALL_NFO_STOCK_OPTIONS") === "ALL_NFO_STOCK_OPTIONS";
  const effShadow = cur.segments_shadow, effLive = cur.segments_live;
  const [segShadow, setSegShadow] = useState(effShadow?.switches || { NFO_STOCK: true, NFO_INDEX: true, MCX: true });
  const [liveMode, setLiveMode] = useState(effLive?.source === "SET" ? "SET" : "LEGACY");
  const [segLive, setSegLive] = useState(effLive?.switches || { NFO_STOCK: false, NFO_INDEX: false, MCX: false });
  const save = () => onChange("options-limits", () => api.post("/risk/limits", {
    limits: { broker_account_id: account.id, options_instrument_access: access,
      options_segments_shadow: segShadow,
      // LIVE: explicit switches, or null = keep the per-underlying rules (nothing changes for LIVE)
      options_segments_live: liveMode === "SET" ? segLive : null,
      options_allowlist: names.split(",").map((n) => n.trim()).filter(Boolean),
      options_mcx_access: mcxAccess,
      options_mcx_allowlist: mcxNames.split(",").map((n) => n.trim()).filter(Boolean),
      options_max_lots_per_order: Number(maxLots), options_max_pairs_per_trade: Number(maxPairs),
      options_max_open_trades: Number(maxOpen), options_daily_loss_limit: Number(loss) } })
    .then(() => { toast.success("Options limits saved"); setEdit(false); return onSaved && onSaved(); }));
  const editBtn = <button type="button" className="ml-auto text-slate-500 hover:text-[var(--term-primary)]" onClick={() => setEdit(true)}
    data-testid={`options-limits-edit-${account.id}`} title="Edit limits"><Pencil className="h-3.5 w-3.5" /></button>;
  const loss0 = !Number(cur.options_daily_loss_limit);
  return (
    <div data-testid={`options-allowlist-${account.id}`}>
      {!edit ? <>
        <div className={row}><span className="w-44 font-semibold">SHADOW segments</span>
          <span className="mono" data-testid={`options-segments-shadow-${account.id}`}>{segmentsText(effShadow)}</span>{editBtn}</div>
        <div className={row}><span className="w-44 font-semibold">LIVE segments</span>
          <span className="mono" data-testid={`options-segments-live-${account.id}`}>{segmentsText(effLive)}</span></div>
        <div className={row}><span className="w-44 font-semibold">{effLive?.source === "LEGACY" ? "LIVE instruments" : "Instruments (legacy)"}</span>
          <span className="mono" data-testid={`options-access-mode-${account.id}`}>{allNfo ? "All NFO stock options"
            : <>Selected: {(cur.options_allowlist || []).join(", ") || <span className="text-[#9a7200]">none — no NFO options</span>}</>}</span></div>
        <div className={row}><span className="w-44 font-semibold">MCX options</span>
          <span className="mono" data-testid={`options-mcx-mode-${account.id}`}>{MCX_LABEL(cur)}</span></div>
        <div className={row}><span className="w-44 font-semibold">Size</span>
          <span className="mono">{cur.options_max_lots_per_order ?? 1} lot/order · {cur.options_max_pairs_per_trade ?? 1} pair(s)/trade · {cur.options_max_open_trades ?? 1} open trade(s)</span></div>
        <div className={row}><span className="w-44 font-semibold">Daily loss limit</span>
          <span className="mono">{loss0 ? <span className="text-[#9a7200]">not set (SHADOW only; LIVE refuses)</span> : `₹${Number(cur.options_daily_loss_limit).toLocaleString("en-IN")}`}</span></div>
      </> : (
        <div className="space-y-1.5 py-1.5 border-t border-[var(--term-border)] text-xs">
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">SHADOW segments</span>
            <SegmentChecks value={segShadow} onChange={setSegShadow} testid={`options-seg-shadow-${account.id}`} /></div>
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">LIVE segments</span>
            <select value={liveMode} onChange={(e) => setLiveMode(e.target.value)} className={`${inputCls} w-56`} data-testid={`options-seg-live-mode-${account.id}`}>
              <option value="LEGACY">Per-underlying rules below (unchanged)</option>
              <option value="SET">Segment switches</option>
            </select>
            {liveMode === "SET" && <SegmentChecks value={segLive} onChange={setSegLive} testid={`options-seg-live-${account.id}`} />}</div>
          <div className="text-[10px] text-slate-500 pl-44">Every contract is still checked against the account's own Zerodha instrument master; the account's options SHADOW / LIVE enables, size and loss limits apply as before.</div>
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">{liveMode === "LEGACY" ? "LIVE instruments" : "Instruments (legacy)"}</span>
            <select value={access} onChange={(e) => setAccess(e.target.value)} className={`${inputCls} w-56`} data-testid={`options-access-select-${account.id}`}>
              <option value="ALL_NFO_STOCK_OPTIONS">All NFO stock options</option>
              <option value="SELECTED">Selected underlyings</option>
            </select></div>
          {access === "SELECTED" && <div className="flex items-center gap-2"><span className="w-44 font-semibold">Underlyings</span>
            <input value={names} onChange={(e) => setNames(e.target.value.toUpperCase())} placeholder="e.g. ICICIBANK, RELIANCE"
              className={`${inputCls} max-w-xs`} data-testid={`options-allowlist-input-${account.id}`} /></div>}
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">MCX options</span>
            <select value={mcxAccess} onChange={(e) => setMcxAccess(e.target.value)} className={`${inputCls} w-56`} data-testid={`options-mcx-select-${account.id}`}>
              <option value="ALL_MCX_OPTIONS">All MCX options</option>
              <option value="SELECTED">Selected commodities</option>
              <option value="NONE">Off</option>
            </select>
            <span className="text-[10px] text-slate-500">independent of the NFO choice above</span></div>
          {mcxAccess === "SELECTED" && <div className="flex items-center gap-2"><span className="w-44 font-semibold">MCX commodities</span>
            <input value={mcxNames} onChange={(e) => setMcxNames(e.target.value.toUpperCase())} placeholder="e.g. CRUDEOIL, NATURALGAS"
              className={`${inputCls} max-w-xs`} data-testid={`options-mcx-input-${account.id}`} /></div>}
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">Max lots per order</span>
            <input value={maxLots} onChange={(e) => setMaxLots(e.target.value)} className={`${inputCls} w-16`} data-testid={`options-max-lots-${account.id}`} /></div>
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">Max pairs per trade</span>
            <input value={maxPairs} onChange={(e) => setMaxPairs(e.target.value)} className={`${inputCls} w-16`} data-testid={`options-max-pairs-${account.id}`} /></div>
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">Max open trades</span>
            <input value={maxOpen} onChange={(e) => setMaxOpen(e.target.value)} className={`${inputCls} w-16`} data-testid={`options-max-open-${account.id}`} /></div>
          <div className="flex items-center gap-2"><span className="w-44 font-semibold">Daily loss limit ₹</span>
            <input value={loss} onChange={(e) => setLoss(e.target.value)} className={`${inputCls} w-28`} data-testid={`options-daily-loss-${account.id}`} />
            <span className="text-[10px] text-slate-500">0 = not set: SHADOW allowed, LIVE refused</span></div>
          <div className="flex items-center gap-2 pl-44">
            <button type="button" className={primaryBtn} disabled={!!busy} onClick={save} data-testid={`options-limits-save-${account.id}`}>Save limits</button>
            <button type="button" className={ghostBtn} onClick={() => setEdit(false)}>Cancel</button>
            <span className="text-[10px] text-slate-500">LIVE capital limits are the account's existing LIVE limits (futures + options together).</span>
          </div>
        </div>
      )}
    </div>
  );
}

// verified option quantities (the owner's broker lot x measured units), and a verify action that uses
// the owner's own connected session and the broker's READ-ONLY margin calculator
function Quantity({ account, busy, onChange, onSaved }) {
  const [und, setUnd] = useState("");
  const verify = () => onChange("options-verify", () => api.post(`/admin/broker-accounts/${account.id}/options/verify-quantity`, { underlying: und })
    .then((r) => { toast.success(`${r.data.underlying}: ${r.data.proposed_broker_quantity} × ${r.data.units_per_quantity} verified`); setUnd(""); return onSaved(); }));
  const list = account.quantity_verified || [];
  return (
    <div className={row} data-testid={`options-quantity-${account.id}`}>
      <span className="w-44 font-semibold">Quantity</span>
      <span className="flex flex-wrap gap-x-3 mono">
        {list.length ? list.map((q) => <span key={`${q.segment}-${q.underlying}`}>{q.underlying} {q.lot} × {Number(q.units).toFixed(2)} <b className="text-[#008a04]">VERIFIED</b>
          <span className="text-slate-500"> ({q.verified_at ? dateTime(q.verified_at) : "—"})</span></span>)
          : <span className="text-slate-500 not-italic">none yet — verified automatically (read-only) on first use</span>}
      </span>
      <span className="ml-auto flex items-center gap-1">
        <input value={und} onChange={(e) => setUnd(e.target.value.toUpperCase())} placeholder="Underlying" className={`${inputCls} w-28`} data-testid={`options-verify-input-${account.id}`} />
        <button type="button" className={ghostBtn} disabled={!!busy || !und.trim()} onClick={verify} data-testid={`options-verify-${account.id}`}
          title="Uses the account owner's connected Zerodha session and the read-only margin calculator">Verify…</button>
      </span>
    </div>
  );
}

export default function OptionsAccess({ userId, user, busy, onChange, onData }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [change, setChange] = useState(null);
  const [reason, setReason] = useState("");
  const hasLive = (user?.roles || []).includes("LIVE_TRADER");

  const load = useCallback(() => api.get("/admin/options-access").then((r) => setData(r.data)).catch((e) => setErr(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  // Options ANALYSIS runs in its own worker process (options_service); its switch lives in THAT
  // process's environment, so the main backend cannot report it. Ask the worker itself.
  const [analysis, setAnalysis] = useState(null);
  useEffect(() => {
    optionsApi.get("/health")
      .then((r) => setAnalysis({ reachable: true, enabled: !!r.data?.enabled }))
      .catch(() => setAnalysis({ reachable: false, enabled: false }));
  }, []);

  // the Access overview (AccessOverview) reads the same answer - one request, refreshed after every change here
  useEffect(() => { if (onData) onData(data, analysis); }, [data, analysis, onData]);
  const accounts = (data?.accounts || []).filter((a) => a.owner_user_id === userId);
  const dep = data?.deployment || {};

  const submit = async () => {
    await onChange(`options-${change.feature.toLowerCase()}`,
      () => api.post(`/admin/broker-accounts/${change.account.id}/options/${change.feature}`,
        { enabled: change.enabled, reason: reason.trim() }).then(() => {
          toast.success(`${change.label} ${change.enabled ? "enabled" : "disabled"}`);
          setChange(null); setReason(""); return load();
        }));
  };
  const onOff = (v) => (v ? "on" : "off");

  return (
    <section className="space-y-3 border-t border-[var(--term-border)] pt-4" data-testid="options-access">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-bold">Options keys, segments and limits</h3>
        <span className="text-[10px] text-slate-500 mono" data-testid="options-deployment">
          Server: <span data-testid="options-analysis-server">Analysis {analysis == null ? "…" : !analysis.reachable ? "unreachable" : onOff(analysis.enabled)}</span>
          {" "}· Hedging {onOff(dep.options_hedge_enabled)} · Writes <b className={dep.options_hedge_broker_writes ? "text-[#FF3B30]" : ""}>{onOff(dep.options_hedge_broker_writes)}</b>
          {" "}· Options LIVE <b className={dep.options_live_trading_enabled ? "text-[#FF3B30]" : ""}>{onOff(dep.options_live_trading_enabled)}</b>
        </span>
      </div>
      <ErrorBox>{err}</ErrorBox>
      {data && !accounts.length && <div className="term-panel-2 rounded-sm p-3 text-xs text-slate-500">No broker account linked.</div>}
      {accounts.map((a) => (
        <div key={a.id} className="term-panel-2 rounded-sm p-3" data-testid={`options-account-${a.id}`}>
          <div className="flex items-center gap-2 pb-2">
            <Layers className="h-4 w-4 text-[#2563EB]" />
            <span className="text-xs font-semibold">{a.broker_user_id} · {(a.broker || "broker").replace(/^./, (c) => c.toUpperCase())} · {a.link_status === "ACTIVE" ? "linked" : a.link_status?.toLowerCase()}</span>
          </div>
          <TradingProducts account={a} busy={busy} onChange={onChange} onSaved={load} />
          <AccessMatrix account={a} />
          <FuturesSegments account={a} busy={busy} onChange={onChange} onSaved={load} />
          {KEY_GROUPS.map(([title, sub, keys]) => (
            <div key={title} className="pt-3" data-testid={`options-group-${keys[0]}-${a.id}`}>
              <div className="text-xs font-bold uppercase tracking-wide text-[var(--term-secondary)]">{title}</div>
              <div className="text-[11px] text-slate-500 pb-1">{sub}</div>
              {FEATURES.filter((f) => keys.includes(f.key)).map((f) => {
                const eff = a.effective?.[f.key] || {};
                const blocked = eff.enabled ? null : blockedReason(f.key, a, hasLive);
                return (
                  <div key={f.key} className={row}>
                    <span className="w-56 font-semibold">{f.label}</span>
                    <span className={`w-24 text-[11px] font-bold ${eff.enabled ? "text-[#008a04]" : blocked ? "text-[#8a6500]" : "text-slate-500"}`} data-testid={`options-state-${f.key}-${a.id}`}>
                      {eff.enabled ? "ENABLED" : "DISABLED"}</span>
                    <span className={`flex-1 min-w-[12rem] text-[11px] ${blocked ? "text-[#8a6500]" : "text-slate-500"}`} data-testid={`options-hint-${f.key}-${a.id}`}>
                      {blocked ? `Can't enable yet: ${blocked}` : (eff.source === "FOLLOWS_IRON_CONDOR_SCANNER" ? "not set: follows the Iron Condor scan" : f.hint)}</span>
                    <span className="ml-auto"><Switch on={eff.enabled} disabled={!!busy || (!!blocked && !eff.enabled)} testId={`options-toggle-${f.key}-${a.id}`}
                      title={blocked || (eff.enabled ? `Disable ${f.label}` : `Enable ${f.label}`)}
                      onClick={() => { setReason(""); setChange({ account: a, feature: f.key, label: f.label, enabled: !eff.enabled, danger: f.danger }); }} /></span>
                  </div>
                );
              })}
            </div>))}
          <div className="pt-3 text-xs font-bold uppercase tracking-wide text-[var(--term-secondary)]">Segments, instruments and limits</div>
          <OptionsLimits account={a} busy={busy} onChange={onChange} onSaved={load} />
          <Quantity account={a} busy={busy} onChange={onChange} onSaved={load} />
          {a.last_change && <div className="pt-2 text-[10px] text-slate-500" data-testid={`options-last-change-${a.id}`}>
            Last change: {FIELD_LABEL[a.last_change.field] || a.last_change.field} {typeof a.last_change.value === "boolean" ? (a.last_change.value ? "enabled" : "disabled") : `→ ${a.last_change.value}`}
            {" "}by {a.last_change.by} · {dateTime(a.last_change.at)}{a.last_change.reason ? ` · “${a.last_change.reason}”` : ""}</div>}
        </div>
      ))}
      <Dialog open={!!change} onOpenChange={(open) => { if (!open && !busy) { setChange(null); setReason(""); } }}>
        <DialogContent className="term-panel text-[var(--term-primary)]" data-testid="options-access-dialog">
          <DialogHeader><DialogTitle>{change?.enabled ? "Enable" : "Disable"} {change?.label}</DialogTitle>
            <DialogDescription>{change?.danger && change?.enabled
              ? `This authorises ${change.label} on ${change.account.broker_user_id}. Orders still need every server switch and check.`
              : "This changes the account's Options flag only. It does not bypass any execution safeguard."}</DialogDescription></DialogHeader>
          <Field label="Reason (recorded in audit history)"><input autoFocus value={reason} maxLength={500}
            onChange={(e) => setReason(e.target.value)} className={inputCls} data-testid="options-access-reason" /></Field>
          <div className="flex justify-end gap-2"><button className={ghostBtn} onClick={() => setChange(null)}>Cancel</button>
            <button disabled={!!busy || reason.trim().length < 3} className={change?.enabled && !change?.danger ? primaryBtn : dangerBtn}
              onClick={submit} data-testid="options-access-confirm">Confirm</button></div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
